import { useState } from "react";
import MailIcon from "@mui/icons-material/Mail";
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useLocales from "../../../hooks/useLocales";
import {
  useLoginMutation,
  useResetPasswordMutation,
} from "../../../features/api/Api";
import { useAppDispatch } from "../../../app/hooks";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  IResetPasswordInitials,
  resetPasswordInitials,
} from "../../../interfaces/User";
import Swal from "sweetalert2";
import { setCredentials } from "../../../features/auth/AuthSlice";
import { Form, Formik } from "formik";
import { validationSchema } from "./common/validations";
import {
  AutocompleteDropDown,
  CodeInput,
  TextInput,
} from "../../../components";
import { locale, PasswordFrequencyOptions } from "../../../utils";
import RecaptchaInput from "../../../components/inputs/RecaptchaInput";
import VerifyDialog from "../login/common/VerifyDialog";

const ResetPassword = () => {
  const [t, i18next] = useTranslation();
  const { translate } = useLocales();
  const [resetPassword, isSubmitting] = useResetPasswordMutation();
  const [login] = useLoginMutation();
  const dispatch = useAppDispatch();
  const history = useNavigate();
  const [searchParams]: any = useSearchParams();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const submitHandler = (
    values: IResetPasswordInitials,
    { setSubmitting }: any
  ) => {
    resetPassword({
      email: values.email,
      code: values.code,
      password: values.password,
      // changePasswordPeriod: values.changePasswordPeriod,
    })
      .unwrap()
      .then((data: any) => {
        if (data.isSuccessful) {
          Swal.fire({
            title: translate("actions.password_reset_success"),
            icon: "success",
            confirmButtonText: translate("actions.log_in"),
          }).then((result: any) => {
            if (result.isConfirmed) {
              login({
                email: values.email,
                password: values.password,
                customerCode: values.customerCode,
                clientId: "DijitalHizmet",
              })
                .unwrap()
                .then((data: any) => {
                  if (
                    data.isSuccessful &&
                    typeof data.data.result === "boolean"
                  ) {
                    setOpenModal(true);
                    setPhoneNumber(data.data.phoneNumber);
                  } else if (
                    data.isSuccessful &&
                    typeof data.data === "object"
                  ) {
                    dispatch(setCredentials(data.data.accessToken));
                    history("/");
                  }
                })
                .catch((error: any) =>
                  Swal.fire({
                    icon: "error",
                    title: "Değerli Müşterimiz,",
                    text: error.data.Errors,
                  })
                );
            }
          });
          setSubmitting(false);
        } else {
          Swal.fire({
            title: translate("actions.password_reset_error"),
            icon: "error",
            confirmButtonText: translate("actions.cancel"),
            text: data.Errors,
          });
        }
      })
      .catch((error: any) => {
        Swal.fire({
          icon: "error",
          title: "Değerli Müşterimiz,",
          text: error.data.Errors,
          confirmButtonText: translate("actions.ok"),
        });
        setSubmitting(false);
      });
  };

  return (
    <div className={`${process.env.REACT_APP_AUTH_CLASSNAME}`}>
      <Container>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: "100vh" }}
          spacing={3}
        >
          <Grid item md={5}>
            <div className="card card-login">
              {isSubmitting ? (
                <>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <div>
                      <img src="/img/logo.svg" alt="Fixity" />
                    </div>
                    <div>
                      <Typography variant="h5" align="right">
                        {translate("resources.resetPassword.root")}
                      </Typography>
                    </div>
                  </Grid>
                  <Formik
                    initialValues={{
                      ...resetPasswordInitials,
                      email: searchParams.get("email")!,
                      customerCode: searchParams.get("customerCode")!,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={submitHandler}
                  >
                    {(props) => (
                      <Form>
                        <Grid container spacing={3}>
                          <Grid item md={12}>
                            <div
                              style={{
                                textAlign: "center",
                              }}
                            >
                              <MailIcon fontSize="large" color="success" />
                            </div>
                          </Grid>
                          <Grid item md={12}>
                            <Typography
                              sx={{
                                textAlign: "center",
                                fontWeight: "bold",
                                marginBottom: 1,
                              }}
                            >
                              {translate("resources.resetPassword.mail_info")}
                            </Typography>
                          </Grid>
                          <Grid item md={12}>
                            <CodeInput name="code" />
                          </Grid>
                          <Grid item md={12}>
                            <TextInput
                              name="customerCode"
                              label={translate(
                                "resources.resetPassword.customerCode"
                              )}
                              type="customerCode"
                              disabled
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextInput
                              name="email"
                              label={translate("resources.resetPassword.email")}
                              type="email"
                              disabled
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextInput
                              name="password"
                              label={translate(
                                "resources.resetPassword.password"
                              )}
                              type="password"
                            />
                          </Grid>
                          <Grid item md={12}>
                            <TextInput
                              name="passwordCompare"
                              label={translate(
                                "resources.resetPassword.password_again"
                              )}
                              type="password"
                            />
                          </Grid>
                          {/* <Grid item md={12}>
                            <AutocompleteDropDown
                              name="changePasswordPeriod"
                              label={translate(
                                "resources.changePassword.password_frequency"
                              )}
                              options={locale(
                                PasswordFrequencyOptions,
                                "resources.changePassword."
                              )}
                              disableClearable={true}
                            />
                          </Grid> */}
                          <Grid item md={12}>
                            <RecaptchaInput name="recaptcha" />
                          </Grid>
                          <Grid item md={12}>
                            <Box sx={{ mx: "auto", width: 200 }}>
                              <Button
                                className="btn btn-default"
                                type="submit"
                                sx={{ mx: "auto", width: 200 }}
                              >
                                {translate("resources.resetPassword.create")}
                              </Button>
                            </Box>
                            <Box sx={{ mx: "auto", width: 200 }}>
                              <p style={{ marginTop: "30px" }}>
                                {translate(
                                  "resources.users.forgot_password.already_registered"
                                )}
                                <Link to="/login">
                                  {translate(
                                    "resources.users.forgot_password.login"
                                  )}
                                </Link>
                              </p>
                            </Box>
                          </Grid>
                        </Grid>
                        {openModal ? (
                          <VerifyDialog
                            open={openModal}
                            message=""
                            phoneNumber={phoneNumber}
                          />
                        ) : null}
                      </Form>
                    )}
                  </Formik>
                </>
              ) : (
                <CircularProgress />
              )}
            </div>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default ResetPassword;
